import { type ReactNode } from 'react';

import { AppBuilderIcon } from '@/icons/Icons';
import { ReactComponent as IconAbout } from '@/images/icons/icon-about.svg';
import { ReactComponent as IconChangelog } from '@/images/icons/icon-changelog.svg';
import { ReactComponent as IconCustomers } from '@/images/icons/icon-customers.svg';
import { ReactComponent as IconDataset } from '@/images/icons/icon-dataset.svg';
import { ReactComponent as IconDocs } from '@/images/icons/icon-docs.svg';
import { ReactComponent as IconDownload } from '@/images/icons/icon-download.svg';
import { ReactComponent as IconExplore } from '@/images/icons/icon-explore.svg';
import { ReactComponent as IconForTeams } from '@/images/icons/icon-for-teams.svg';
import { ReactComponent as IconGroupedColumnChart } from '@/images/icons/icon-grouped-column-chart.svg';
import { ReactComponent as IconIntegrations } from '@/images/icons/icon-integrations.svg';
import { ReactComponent as IconMagic } from '@/images/icons/icon-magic.svg';
import { ReactComponent as IconMarketing } from '@/images/icons/icon-marketing.svg';
import { ReactComponent as IconNotebooks } from '@/images/icons/icon-notebooks.svg';
import { ReactComponent as IconBlog } from '@/images/icons/icon-our-blog.svg';
import { ReactComponent as IconPartners } from '@/images/icons/icon-partners.svg';
import { ReactComponent as IconPin } from '@/images/icons/icon-pin.svg';
import { ReactComponent as IconSales } from '@/images/icons/icon-sales.svg';
import { ReactComponent as IconSemanticModels } from '@/images/icons/icon-semantic-models.svg';
import { ReactComponent as IconTemplates } from '@/images/icons/icon-templates.svg';
import { ReactComponent as IconExploratoryAnalysis } from '@/images/icons/icon-use-case-exploratory-analysis.svg';
import { DOCS_LINKS, INTERNAL_LINKS } from '@/utils';

export type NavItem =
  | {
      dropdown: {
        lists: Array<{
          children: Array<{
            description: string;
            icon: ReactNode;
            title: string;
            to: string;
          }>;
          title: string;
        }>;
      };
      icon: ReactNode;
      title: string;
    }
  | {
      icon: ReactNode;
      title: string;
      to: string;
    };

export const leftNav: Array<NavItem> = [
  {
    title: 'Platform',
    icon: null,
    dropdown: {
      lists: [
        {
          title: 'Products',
          children: [
            {
              icon: <IconNotebooks />,
              title: 'Notebooks',
              to: INTERNAL_LINKS.Notebooks,
              description: 'Powerful analysis without the silos'
            },
            {
              icon: <AppBuilderIcon />,
              title: 'Data apps',
              to: INTERNAL_LINKS.AppBuilder,
              description:
                'Build and share insights with fast and flexible tools'
            },
            {
              icon: <IconExplore />,
              title: 'Explore',
              to: INTERNAL_LINKS.Explore,
              description: 'A new approach to self-serve'
            },
            {
              icon: <IconDownload />,
              title: 'Embedded analytics',
              to: INTERNAL_LINKS.EmbeddedAnalytics,
              description: 'Ship secure, customer-facing data experiences'
            }
          ]
        },
        {
          title: 'Capabilities',
          children: [
            {
              icon: <IconMagic />,
              title: 'AI-powered',
              to: INTERNAL_LINKS.Magic,
              description:
                'Faster, easier, more accurate — powered by leading LLMs'
            },
            {
              icon: <IconSemanticModels />,
              title: 'Trusted data',
              to: INTERNAL_LINKS.SemanticModels,
              description:
                'Consistent definitions of data that don’t limit your analysis'
            },
            {
              icon: <IconForTeams />,
              title: 'Collaborative intelligence',
              to: INTERNAL_LINKS.ForTeams,
              description:
                'Bring everyone together in one collaborative workspace'
            },
            {
              icon: <IconExploratoryAnalysis />,
              title: 'Exploratory analysis',
              to: INTERNAL_LINKS.UseCaseExploratoryAnalysis,
              description:
                'Go from quick question to deep dive to data app all in one place'
            },
            {
              icon: <IconIntegrations />,
              title: 'Instant integration',
              to: INTERNAL_LINKS.Integrations,
              description: 'Out-of-the-box connections and flexible APIs'
            }
          ]
        }
      ]
    }
  },
  {
    title: 'Solutions',
    icon: null,
    dropdown: {
      lists: [
        {
          title: 'By team',
          children: [
            {
              icon: <IconGroupedColumnChart />,
              title: 'Data leader',
              to: INTERNAL_LINKS.SolutionsDataLeaders,
              description: 'Focus your team and scale answers'
            },
            {
              icon: <IconDataset />,
              title: 'Product',
              to: INTERNAL_LINKS.SolutionsProductAnalytics,
              description: 'Build your product with data, not gut feels'
            },
            {
              icon: <IconMarketing />,
              title: 'Marketing',
              to: INTERNAL_LINKS.SolutionsMarketing,
              description: 'Turn scattered data into clear growth opportunities'
            },
            {
              icon: <IconSales />,
              title: 'Sales',
              to: INTERNAL_LINKS.SolutionsSales,
              description: 'Clear pipeline. Confident forecasts'
            }
          ]
        }
      ]
    }
  },
  {
    title: 'Enterprise',
    icon: null,
    to: INTERNAL_LINKS.Enterprise
  }
];

export const rightNav: Array<NavItem> = [
  {
    title: 'Resources',
    icon: null,
    dropdown: {
      lists: [
        {
          title: 'Get started',
          children: [
            {
              title: 'Templates',
              to: INTERNAL_LINKS.Templates,
              icon: <IconTemplates />,
              description: 'Jumpstart with pre-built projects'
            },
            {
              title: 'Hex Foundations',
              to: DOCS_LINKS.HexFoundations,
              icon: <IconAbout />,
              description: 'Video series'
            },
            {
              title: 'Docs',
              to: DOCS_LINKS.Home,
              icon: <IconDocs />,
              description: 'Resources and product guides'
            },
            {
              title: 'Changelog',
              to: DOCS_LINKS.Changelog,
              icon: <IconChangelog />,
              description: 'Product updates'
            }
          ]
        },
        {
          title: 'Inspiration',
          children: [
            {
              title: 'Blog',
              to: INTERNAL_LINKS.Blog,
              icon: <IconBlog />,
              description: 'From data teams to data teams'
            },
            {
              title: 'Resources & Events',
              to: INTERNAL_LINKS.Resources,
              icon: <IconPin />,
              description: 'Learn and connect with peers'
            },
            {
              title: 'Customer stories',
              to: INTERNAL_LINKS.Customers,
              icon: <IconCustomers />,
              description: 'Empowering the best data teams'
            },
            {
              title: 'Partners',
              to: INTERNAL_LINKS.Partners,
              icon: <IconPartners />,
              description: 'Learn more about our partnerships'
            }
          ]
        }
      ]
    }
  },
  {
    title: 'Pricing',
    icon: null,
    to: INTERNAL_LINKS.Pricing
  }
];
